// core
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot, CanActivate, NavigationExtras, Router, RouterStateSnapshot, UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// campfire
import { HandoverService, PartnerBrandService } from '@campfire-nx/api';

// services
import { AuthService } from '@open-portal-app/core/services/auth.service';
import { RequestService } from '@open-portal-app/request/services/request.service';

import { APPOINTMENT_BOOKING_TOPICS } from '@open-portal-app/help/constants/help.constants';

@Injectable({
  providedIn: 'root'
})
export class HandoverGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private handoverService: HandoverService,
    private partnerBrandService: PartnerBrandService,
    private requestService: RequestService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const params: any = route.queryParams;

    if (!params && !params.jwt) {
      return false;
    }

    this.authService.endSession();

    // set redirect parameter
    if (params.partner && params.jwt) {
      // authenticated handover
      this.authService.setToken(params.jwt);
      if (params.key) {
        this.authService.setApiKey(params.key);
      }

      this.partnerBrandService.setPartner(params.partner);

      if (params.redirectBack) {
        this.partnerBrandService.setAttribute('redirectBack', params.redirectBack, 'partner');
      }

      if (params.action === 'endorse' && params.policy_slug) {
        this.router.navigate([`/endorsement/${params.policy_slug}`]);
      }

      // Appointment and Support related handovers
      if (params.action === 'help_flow') {
        if (params.payload) {
          const parsedPayload = JSON.parse(params.payload);

          if (
            parsedPayload.entity_ref &&
            parsedPayload.option === 'existing_claim'
          ) {
            const requestDetails = {
              ...APPOINTMENT_BOOKING_TOPICS['existing-claim'],
              entity_ref: parsedPayload.entity_ref
            };

            this.requestService.start(requestDetails);
            return true;
          } else {
            this.router.navigate(['/support']);
            return true;
          }
        } else {
          const navigationExtras: NavigationExtras = {
            queryParams: {
              subject: params.subject,
              'customer-intent': params['customer-intent']
            }
          };
          this.router.navigate(['/support'], navigationExtras);
          return true;
        }
      }

      // View Policy Page
      if (params.action === 'policy' && params.ref && params.entity) {
        const entity = params.entity.replace('-policy', '');

        this.router.navigate([`/policy/${entity}/${params.ref}`]);
        return true;
      }

      if (params.action === 'missed_payment') {
        this.router.navigate([`/policy/payment/retry/${params.ref}`]);
        return true;
      }

      // Default routing to dashboard
      this.router.navigate(['/dashboard']);
      return true;
    }
    return this.handoverService.get(params)
      .pipe(
        map((response: any) => {
          if (response.token) {
            this.authService.setToken(response.token);
          }

          if (params.jwt) {
            this.authService.setCustomerToken(params.jwt);
          }

          if (params.key) {
            this.authService.setApiKey(params.key);
          }

          if (params.redirectBack) {
            response.redirectBack = params.redirectBack;
          }
          this.partnerBrandService.setHandoverData(response);
          this.partnerBrandService.addPartnerScripts();

          if (params.campaign) {
            this.router.navigateByUrl(`/overview?campaign=${params.campaign}`);
            return true;
          }

          // Manage handover scenarios based on 'response.payload' first
          // This is done before 'action' based checks because these scenarios
          //  often have a default action set as 'dashboard'
          if (response.payload && response.payload.resume_quote && response.payload.entity_ref) {
            this.router.navigate([`/quote/${response.payload.entity_ref}`]);
            return true;
          }
          if (response.payload && response.payload.policy_update) {
            this.router.navigate([`/endorsement/${response.payload.policy_slug}`]);
            return true;
          }
          if (response.payload && response.payload.policy_cancel) {
            this.router.navigate([`/cancellation/${response.payload.policy_slug}`]);
            return true;
          }

          // Action based checks

          if (response.action === 'dashboard') {
            // Handle Central hands
            if (response.payload && response.payload.policy_update) {
              this.router.navigate([`/endorsement/${response.payload.policy_slug}`]);
              return true;
            }

            if (response.payload && response.payload.policy_cancel) {
              this.router.navigate([`/cancellation/${response.payload.policy_slug}`]);
              return true;
            }

            if (response.payload && response.payload.policy_prerenewals) {
              this.router.navigate([`/prerenewal/${response.payload.policy_slug}`]);
              return true;
            }

            // Dashboard is the primary experience for a logged in user
            this.router.navigate(['/dashboard']);
            return true;
          }

          if (response.action === 'help_flow') {
            const navigationExtras: NavigationExtras = {
              queryParams: {
                subject: params.subject,
                'customer-intent': params['customer-intent']
              }
            };

            this.router.navigate(['/support'], navigationExtras);
            return true;
          }

          if (response.action === 'appointment') {
            const { payload } = response;

            this.router.navigate(['/support']); // TODO to update and below during onramp work

            // Load quote provided from handover
            // if (payload.class && payload.type) {
            //   this.router.navigate([
            //     `/support/${payload.type}/${payload.class}/${response[payload.type + '_slug']}`
            //   ]);
            // // Coming from /start-quote page at the moment, can change in the future
            // } else if (!payload.class && payload.type) {
            //   this.router.navigate([`/support/${payload.type}/`])
            // // Coming from You are covered page or Contact us on Huddle website
            // } else {
            //   this.router.navigate([`/support`])
            // }
            return true;
          }

          if (response.action === 'member_validation') {
            this.router.navigate(['/user/member-validation']);
            return true;
          }

          if (response.action === 'policy' && response.ref && response.entity) {
            const entity = response.entity.replace('-policy', '');

            this.router.navigate([`/policy/${entity}/${response.ref}`]);
            return true;
          }

          if (response.quote_slug && response.product) {
            const customParameters = params.customParameters ? `?${params.customParameters}` : '';
            this.router.navigateByUrl(`/quote/${response.quote_slug}${customParameters}`);
            return true;
          }

          return false;
        }),
        catchError((err) => {
          if (err.status === 410) {
            // magiclink expired
            this.router.navigate(['/magic-link']);
          }
          if (err.status === 404) {
            this.router.navigate(['/not-found']);
          }
          if (err.status === 403) {
            this.router.navigate(['/bad-request']);
          }
          if (err.status === 400) {
            this.router.navigate(['/bad-request']);
          }
          return of(false);
        })
      );
  }
}
